import React, { useCallback, useEffect, useState } from 'react';
import { HiKey, HiRefresh, HiShieldCheck, HiInformationCircle, HiCheckCircle, HiXCircle } from 'react-icons/hi';
import apiClient from '../api/client';
import { useNotification } from '../context/NotificationContext';

function OzonKeySection({ title, icon, hint, fields, verifyUrl, saveUrl, clientId, canEdit, notify, hasKeys }) {
  const [values, setValues] = useState(() => Object.fromEntries(fields.map((f) => [f.key, ''])));
  const [show, setShow] = useState({});
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [result, setResult] = useState(null);

  const setField = (key, val) => { setValues((p) => ({ ...p, [key]: val })); setResult(null); };
  const hasInput = fields.some((f) => values[f.key]?.trim());

  const handleVerify = async () => {
    setVerifying(true); setResult(null);
    try {
      const payload = {};
      fields.forEach((f) => { if (values[f.key]?.trim()) payload[f.payloadKey] = values[f.key].trim(); });
      const { data } = await apiClient.post(verifyUrl.replace(':id', clientId), payload);
      setResult(data);
      notify(data?.message || (data?.valid ? 'Ключи рабочие' : 'Проверка не пройдена'), data?.valid ? 'success' : 'error');
    } catch (err) {
      const msg = err.response?.data?.message || 'Ошибка проверки';
      setResult({ valid: false, message: msg }); notify(msg, 'error');
    } finally { setVerifying(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!hasInput) { notify('Заполните все поля', 'warning'); return; }
    setSaving(true);
    try {
      const payload = {};
      fields.forEach((f) => { payload[f.payloadKey] = values[f.key].trim(); });
      const { data } = await apiClient.patch(saveUrl.replace(':id', clientId), payload);
      notify(data?.message || 'Сохранено', 'success');
      setValues(Object.fromEntries(fields.map((f) => [f.key, ''])));
      setResult(null);
    } catch (err) { notify(err.response?.data?.message || 'Ошибка сохранения', 'error'); }
    finally { setSaving(false); }
  };

  return (
    <div className="wb-card p-5">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-lg">{icon}</span>
        <h3 className="font-semibold">{title}</h3>
        <span className={`ml-auto text-xs font-medium px-2 py-0.5 rounded-full ${hasKeys ? 'bg-[var(--wb-success-bg)] text-[var(--wb-success)]' : 'bg-[var(--wb-surface-muted)] text-[var(--wb-text-muted)]'}`}>
          {hasKeys ? 'подключён' : 'не подключён'}
        </span>
      </div>
      {hint && (
        <div className="flex gap-2 p-3 rounded-lg bg-[var(--wb-surface-muted)] border border-[var(--wb-border)] mb-4">
          <HiInformationCircle className="w-5 h-5 text-[var(--wb-text-muted)] shrink-0 mt-0.5" />
          <p className="text-xs text-[var(--wb-text-secondary)] leading-relaxed">{hint}</p>
        </div>
      )}
      {!canEdit ? (
        <p className="text-sm text-[var(--wb-text-muted)]">Изменение ключей недоступно для вашей роли.</p>
      ) : (
        <form onSubmit={handleSave} className="space-y-3">
          {fields.map((f) => (
            <div key={f.key}>
              <label className="block text-xs text-[var(--wb-text-muted)] mb-1">{f.label}</label>
              <div className="relative">
                <input
                  type={show[f.key] ? 'text' : 'password'}
                  className="wb-input w-full pr-20 font-mono text-sm"
                  placeholder={f.placeholder}
                  value={values[f.key]}
                  onChange={(e) => setField(f.key, e.target.value)}
                  autoComplete="off" spellCheck={false}
                />
                <button type="button" onClick={() => setShow((p) => ({ ...p, [f.key]: !p[f.key] }))}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-[var(--wb-text-muted)] hover:text-[var(--wb-primary)] px-2 py-1">
                  {show[f.key] ? 'Скрыть' : 'Показать'}
                </button>
              </div>
            </div>
          ))}
          {result && (
            <div className={`flex items-start gap-2 p-3 rounded-lg border text-sm ${result.valid ? 'bg-[var(--wb-success-bg)] border-[var(--wb-success)]/30 text-[var(--wb-success)]' : 'bg-[var(--wb-danger-bg)] border-[var(--wb-danger)]/30 text-[var(--wb-danger)]'}`}>
              {result.valid ? <HiCheckCircle className="w-5 h-5 shrink-0 mt-0.5" /> : <HiXCircle className="w-5 h-5 shrink-0 mt-0.5" />}
              <p className="font-medium">{result.message}</p>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <button type="submit" className="wb-btn" disabled={saving || !hasInput}>{saving ? 'Сохранение…' : 'Сохранить'}</button>
            <button type="button" className="wb-btn-secondary" onClick={handleVerify}
              disabled={verifying || saving || (!hasInput && !hasKeys)}>
              {verifying ? 'Проверка…' : hasInput ? 'Проверить' : 'Проверить текущие'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default function ClientCabinetSettings({ clientId }) {
  const { notify } = useNotification();
  const [client, setClient] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [apiToken, setApiToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [syncRunning, setSyncRunning] = useState(false);

  const canEditToken = userRole === 'admin' || userRole === 'user';

  const loadClient = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const [clientsRes, meRes] = await Promise.all([
        apiClient.get('/clients'),
        apiClient.get('/users/me').catch(() => ({ data: {} })),
      ]);
      const list = Array.isArray(clientsRes.data) ? clientsRes.data : [];
      const found = list.find((c) => String(c.id) === String(clientId)) || null;
      setClient(found);
      setUserRole(meRes.data?.user?.role || null);
    } catch {
      setClient(null);
      notify('Не удалось загрузить данные кабинета', 'error');
    } finally {
      setLoading(false);
    }
  }, [clientId, notify]);

  const loadSyncStatus = useCallback(async () => {
    if (!clientId) return;
    try {
      const { data } = await apiClient.get(`/clients/${clientId}/sync-status`);
      setSyncRunning(Boolean(data?.running));
    } catch {
      setSyncRunning(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadClient();
    loadSyncStatus();
  }, [loadClient, loadSyncStatus]);

  useEffect(() => {
    if (!syncRunning) return undefined;
    const timer = setInterval(loadSyncStatus, 5000);
    return () => clearInterval(timer);
  }, [syncRunning, loadSyncStatus]);

  const handleVerify = async () => {
    const trimmed = apiToken.trim();
    if (!trimmed && !client?.has_api_token) {
      notify('Введите токен для проверки', 'warning');
      return;
    }

    setVerifying(true);
    setVerifyResult(null);
    try {
      const payload = trimmed ? { api_token: trimmed } : {};
      const { data } = await apiClient.post(`/clients/${clientId}/api-token/verify`, payload);
      setVerifyResult(data);
      if (data?.valid) {
        notify(data.message || 'Токен рабочий', 'success');
      } else {
        notify(data?.message || 'Токен не прошёл проверку', 'error');
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Не удалось проверить токен';
      setVerifyResult({ valid: false, message });
      notify(message, 'error');
    } finally {
      setVerifying(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const trimmed = apiToken.trim();
    if (!trimmed) {
      notify('Введите новый API-токен', 'warning');
      return;
    }
    setSaving(true);
    try {
      const { data } = await apiClient.patch(`/clients/${clientId}/api-token`, {
        api_token: trimmed,
      });
      setApiToken('');
      setShowToken(false);
      notify(data?.message || 'API-токен сохранён', 'success');
      await loadClient();
      await loadSyncStatus();
      if (data?.initial_sync?.started) {
        setSyncRunning(true);
      }
    } catch (err) {
      notify(err.response?.data?.message || 'Не удалось сохранить токен', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="wb-card p-8 text-center text-sm text-[var(--wb-text-muted)]">
        Загрузка настроек…
      </div>
    );
  }

  if (!client) {
    return (
      <div className="wb-card p-8 text-center text-sm text-[var(--wb-danger)]">
        Кабинет не найден или нет доступа
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="wb-card p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[var(--wb-primary-soft)] flex items-center justify-center shrink-0">
            <HiShieldCheck className="w-5 h-5 text-[var(--wb-primary)]" />
          </div>
          <div className="min-w-0">
            <h2 className="font-semibold text-base">{client.name}</h2>
            <p className="text-sm text-[var(--wb-text-muted)] mt-0.5">
              ID кабинета: {client.id}
            </p>
            <p className="text-sm mt-2">
              API-токен:{' '}
              <span
                className={
                  client.has_api_token
                    ? 'text-[var(--wb-success)] font-medium'
                    : 'text-[var(--wb-danger)] font-medium'
                }
              >
                {client.has_api_token ? 'установлен' : 'не задан'}
              </span>
            </p>
            {syncRunning && (
              <p className="text-xs text-[var(--wb-primary)] mt-2 flex items-center gap-1">
                <HiRefresh className="w-3.5 h-3.5 animate-spin" />
                Идёт фоновая выгрузка данных после смены токена…
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="wb-card p-5">
        <div className="flex items-center gap-2 mb-4">
          <HiKey className="w-5 h-5 text-[var(--wb-primary)]" />
          <h3 className="font-semibold">API-токен Wildberries</h3>
        </div>

        <div className="flex gap-2 p-3 rounded-lg bg-[var(--wb-surface-muted)] border border-[var(--wb-border)] mb-4">
          <HiInformationCircle className="w-5 h-5 text-[var(--wb-text-muted)] shrink-0 mt-0.5" />
          <p className="text-xs text-[var(--wb-text-secondary)] leading-relaxed">
            Токен привязан к этому кабинету (ID {clientId}). Обновите его, если истёк срок
            действия или изменились категории доступа в личном кабинете WB. После сохранения
            запустится повторная выгрузка данных — создавать нового клиента не нужно.
          </p>
        </div>

        {!canEditToken ? (
          <div className="space-y-3">
            <p className="text-sm text-[var(--wb-text-muted)]">
              Изменение API-токена недоступно для вашей роли. Обратитесь к администратору.
            </p>
            {client.has_api_token && (
              <>
                {verifyResult && (
                  <div
                    className={`flex items-start gap-2 p-3 rounded-lg border text-sm ${
                      verifyResult.valid
                        ? 'bg-[var(--wb-success-bg)] border-[var(--wb-success)]/30 text-[var(--wb-success)]'
                        : 'bg-[var(--wb-danger-bg)] border-[var(--wb-danger)]/30 text-[var(--wb-danger)]'
                    }`}
                  >
                    {verifyResult.valid ? (
                      <HiCheckCircle className="w-5 h-5 shrink-0 mt-0.5" />
                    ) : (
                      <HiXCircle className="w-5 h-5 shrink-0 mt-0.5" />
                    )}
                    <div className="min-w-0">
                      <p className="font-medium">{verifyResult.message}</p>
                      {verifyResult.valid && verifyResult.status && (
                        <p className="text-xs mt-0.5 opacity-80">
                          WB API: {verifyResult.status}
                          {verifyResult.ts ? ` · ${verifyResult.ts}` : ''}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  className="wb-btn-secondary"
                  onClick={handleVerify}
                  disabled={verifying}
                >
                  {verifying ? 'Проверка…' : 'Проверить текущий токен'}
                </button>
              </>
            )}
          </div>
        ) : (
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label htmlFor="api-token" className="block text-xs text-[var(--wb-text-muted)] mb-1">
                Новый API-токен
              </label>
              <div className="relative">
                <input
                  id="api-token"
                  type={showToken ? 'text' : 'password'}
                  className="wb-input w-full pr-24 font-mono text-sm"
                  placeholder="Вставьте токен из личного кабинета WB"
                  value={apiToken}
                  onChange={(e) => {
                    setApiToken(e.target.value);
                    setVerifyResult(null);
                  }}
                  autoComplete="off"
                  spellCheck={false}
                />
                <button
                  type="button"
                  onClick={() => setShowToken(!showToken)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-[var(--wb-text-muted)] hover:text-[var(--wb-primary)] px-2 py-1"
                >
                  {showToken ? 'Скрыть' : 'Показать'}
                </button>
              </div>
            </div>

            {verifyResult && (
              <div
                className={`flex items-start gap-2 p-3 rounded-lg border text-sm ${
                  verifyResult.valid
                    ? 'bg-[var(--wb-success-bg)] border-[var(--wb-success)]/30 text-[var(--wb-success)]'
                    : 'bg-[var(--wb-danger-bg)] border-[var(--wb-danger)]/30 text-[var(--wb-danger)]'
                }`}
              >
                {verifyResult.valid ? (
                  <HiCheckCircle className="w-5 h-5 shrink-0 mt-0.5" />
                ) : (
                  <HiXCircle className="w-5 h-5 shrink-0 mt-0.5" />
                )}
                <div className="min-w-0">
                  <p className="font-medium">{verifyResult.message}</p>
                  {verifyResult.valid && verifyResult.status && (
                    <p className="text-xs mt-0.5 opacity-80">
                      WB API: {verifyResult.status}
                      {verifyResult.ts ? ` · ${verifyResult.ts}` : ''}
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <button type="submit" className="wb-btn" disabled={saving || !apiToken.trim()}>
                {saving ? 'Сохранение…' : 'Сохранить токен'}
              </button>
              <button
                type="button"
                className="wb-btn-secondary"
                onClick={handleVerify}
                disabled={verifying || saving || (!apiToken.trim() && !client.has_api_token)}
              >
                {verifying
                  ? 'Проверка…'
                  : apiToken.trim()
                    ? 'Проверить токен'
                    : 'Проверить текущий токен'}
              </button>
              <button
                type="button"
                className="wb-btn-secondary"
                onClick={() => {
                  setApiToken('');
                  setVerifyResult(null);
                  loadSyncStatus();
                }}
                disabled={saving || verifying}
              >
                Отмена
              </button>
            </div>
          </form>
        )}
      </div>

      <OzonKeySection
        title="Ozon Seller API"
        icon="🟠"
        hint="Client-Id и Api-Key из личного кабинета Ozon Seller (Настройки → API-ключи). Используется для товаров, остатков, финансов и аналитики."
        fields={[
          { key: 'clientId', payloadKey: 'client_id', label: 'Client-Id', placeholder: 'Например: 123456' },
          { key: 'apiKey', payloadKey: 'api_key', label: 'Api-Key', placeholder: 'Вставьте ключ из кабинета Ozon' },
        ]}
        verifyUrl="/clients/:id/ozon-seller/verify"
        saveUrl="/clients/:id/ozon-seller"
        clientId={clientId}
        canEdit={canEditToken}
        notify={notify}
        hasKeys={!!client?.has_ozon_seller || client?.is_demo}
      />

      <OzonKeySection
        title="Ozon Performance API"
        icon="📊"
        hint="Client-Id и Client-Secret из рекламного кабинета Ozon Performance (performance.ozon.ru → API). Используется для рекламных кампаний, ставок и статистики."
        fields={[
          { key: 'clientId', payloadKey: 'client_id', label: 'Client-Id', placeholder: 'Например: abc123@advertising.performance.ozon.ru' },
          { key: 'clientSecret', payloadKey: 'client_secret', label: 'Client-Secret', placeholder: 'Вставьте секрет из кабинета Performance' },
        ]}
        verifyUrl="/clients/:id/ozon-performance/verify"
        saveUrl="/clients/:id/ozon-performance"
        clientId={clientId}
        canEdit={canEditToken}
        notify={notify}
        hasKeys={!!client?.has_ozon_performance || client?.is_demo}
      />

      <OzonKeySection
        title="Авито API (объявления)"
        icon="🟢"
        hint="Client-Id и Client-Secret из developers.avito.ru. Нужны для статистики объявлений и управления продвижением в выдаче."
        fields={[
          { key: 'clientId', payloadKey: 'client_id', label: 'Client-Id', placeholder: 'ID приложения Авито' },
          { key: 'clientSecret', payloadKey: 'client_secret', label: 'Client-Secret', placeholder: 'Secret приложения' },
        ]}
        verifyUrl="/clients/:id/avito/verify"
        saveUrl="/clients/:id/avito"
        clientId={clientId}
        canEdit={canEditToken}
        notify={notify}
        hasKeys={!!client?.has_avito || client?.is_demo}
      />

      <OzonKeySection
        title="Avito Ads API"
        icon="🟧"
        hint="API-ключ из рекламного кабинета ads.avito.com (Настройки → API). Для перформанс-кампаний и сквозной аналитики с Метрикой."
        fields={[
          { key: 'apiKey', payloadKey: 'api_key', label: 'API Key', placeholder: 'Ключ Avito Ads' },
        ]}
        verifyUrl="/clients/:id/avito-ads/verify"
        saveUrl="/clients/:id/avito-ads"
        clientId={clientId}
        canEdit={canEditToken}
        notify={notify}
        hasKeys={!!client?.has_avito_ads || client?.is_demo}
      />
    </div>
  );
}
